import { vec3 } from 'gl-matrix';
import { PathNode } from './PathNode';
import { DEFAULT_OPTIMIZER_MAX_EXPANSIONS, PathOptimizer, PathSegment } from './PathOptimizer';
import { RegionGraph } from './RegionGraph';
import { FindPathOptions, GateResolver, Graph, PathFinder, ResultPath } from '../path-finding';

/**
 * Default A* expansion cap. Sized to comfortably finish any reasonable
 * intra-region path on the demo scene without giving runaway searches free
 * rein. The region graph early-out should make hitting this cap very rare.
 */
const DEFAULT_MAX_EXPANSIONS = 4000;

/** Default search radius for player-initiated navigation (world units). */
const DEFAULT_PLAYER_RADIUS = 200;

/** Default search radius for AI wandering — kept tighter to bound cost. */
const DEFAULT_AI_RADIUS = 60;

const getPathNodePosition = (node: PathNode): vec3 | number[] => node.getPosition();

export class NavigationMeshPathFinder extends PathFinder<PathNode> implements Graph<PathNode> {
  public regionGraph: RegionGraph = new RegionGraph();
  public maxExpansions: number = DEFAULT_MAX_EXPANSIONS;
  public optimizerMaxExpansions: number = DEFAULT_OPTIMIZER_MAX_EXPANSIONS;
  public playerRadius: number = DEFAULT_PLAYER_RADIUS;
  public aiRadius: number = DEFAULT_AI_RADIUS;

  private optimizer: PathOptimizer;

  constructor(
    public gateResolver: GateResolver,
    public voxelSize: number = 1,
  ) {
    super();
    this.setGraph(this);
    this.optimizer = new PathOptimizer(voxelSize);

    this.setHeuristic((node: PathNode) => {
      return vec3.dist(node.getPosition(), this.targetPosition);
    });
  }

  /**
   * Build (or rebuild) the region graph from the given path nodes. Call once
   * after the navmesh has finished loading and any island culling /
   * cross-tile linking has completed.
   */
  public setPathNodes(nodes: PathNode[]): void {
    this.regionGraph.build(nodes);
  }

  public getCost(a: PathNode, b: PathNode): number {
    // World-unit cost matches the world-unit heuristic, making A*
    // admissible/consistent — paths are optimal and look natural.
    // This was previously `1` (greedy best-first) for perf reasons, but
    // with the volgrid floor queries removed from PathOptimizer, per-
    // expansion cost is now low enough that proper A* runs fast.
    return vec3.dist(a.getPosition(), b.getPosition());
  }

  public getNeighbours(element: PathNode): PathNode[] {
    return element.neighbours.filter((node) => {
      return (
        node.reference == '' || node.reference == null || this.gateResolver.testGate(node.reference)
      );
    });
  }

  /**
   * Override of `PathFinder.findPath` that runs the region-graph
   * reachability early-out before invoking A*, and applies the configured
   * `maxExpansions` cap and search radius.
   */
  public override findPath(
    start: PathNode,
    goal: PathNode,
    originPosition: vec3 | number[],
    targetPosition: vec3 | number[],
    options: FindPathOptions<PathNode> = {},
  ): ResultPath<PathNode> {
    if (!this.regionGraph.isReachable(start, goal, this.gateResolver)) {
      return {
        solved: false,
        origin: originPosition,
        target: targetPosition,
        cameFrom: new Map(),
        path: [],
        expansions: 0,
        exhausted: false,
      };
    }

    return super.findPath(start, goal, originPosition, targetPosition, {
      maxExpansions: options.maxExpansions ?? this.maxExpansions,
      maxRadius: options.maxRadius ?? this.playerRadius,
      getPosition: options.getPosition ?? getPathNodePosition,
    });
  }

  /**
   * Run the voxel-level optimizer over a coarse path produced by `findPath`.
   * Thin wrapper around `PathOptimizer.optimize` — kept on this class for
   * API continuity with existing callers.
   */
  public optimizeMovementPath(currentNode: PathNode, path: ResultPath<PathNode>): PathSegment[] {
    return this.optimizer.optimize(
      currentNode,
      path.path,
      path.origin,
      path.target,
      this.optimizerMaxExpansions,
    );
  }
}
